/**
 * "Art & media" — one card in the forge's side panel for everything visual
 * (ADR-037 D6, D8): the game's art, Claude Code's screenshots, files sent
 * from the Vault, styles shipped for players, and the way to FiasWorks
 * Studio. The card is a summary; its panel holds each part in a tab, the
 * same components the side panel used to stack one under another.
 */
import { useState, type ComponentProps } from 'react';
import { Images } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CreativeStudioLink } from '@/components/CreativeStudioLink';
import { useFiasArche } from '@/hooks/useFiasArche';
import { ForgeArtPanel } from './ForgeArtPanel';
import { ForgeScreenshots } from './ForgeScreenshots';
import { ForgeSendFiles } from './ForgeSendFiles';
import { ForgeShipStyle } from './ForgeShipStyle';

export type ArtMediaTab = 'art' | 'screenshots' | 'send' | 'styles';

/** How many art thumbnails the card shows. */
const THUMBS = 4;

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

interface ForgeArtMediaProps {
  forgeId: string;
  archeId: string;
  art: Omit<ComponentProps<typeof ForgeArtPanel>, 'forgeId'>;
  screenshots: ComponentProps<typeof ForgeScreenshots>;
  sendFiles: ComponentProps<typeof ForgeSendFiles>;
  /** Ask Claude in a turn; absent while that is not possible. */
  onAskClaude?: (prompt: string) => void;
}

export function ForgeArtMedia({
  forgeId,
  archeId,
  art,
  screenshots,
  sendFiles,
  onAskClaude,
}: ForgeArtMediaProps) {
  const [tab, setTab] = useState<ArtMediaTab | null>(null);
  const { data: studio } = useFiasArche('creative-studio');
  const from = { kind: 'forge' as const, id: forgeId };

  const assets = art.art?.assets ?? [];
  const shots = screenshots.screenshots.length;
  const unseen = screenshots.unseen ?? 0;
  const filesIn = (status: (s: string) => boolean) =>
    sendFiles.deliveries.filter((d) => status(d.status)).reduce((n, d) => n + d.files.length, 0);
  const sending = filesIn((s) => s === 'pending' || s === 'dispatched');
  const failed = filesIn((s) => s === 'failed');

  return (
    <div className="space-y-2 rounded-lg border p-3" data-testid="forge-art-media">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Art &amp; media</h3>
        <Button size="sm" variant="outline" className="h-7" onClick={() => setTab('art')}>
          <Images className="mr-1 h-3.5 w-3.5" />
          Open
        </Button>
      </div>

      {assets.length > 0 && (
        <button
          type="button"
          className="grid w-full grid-cols-4 gap-1"
          onClick={() => setTab('art')}
          aria-label="Open the game's art"
        >
          {assets.slice(0, THUMBS).map((asset) => (
            <span
              key={asset.name}
              className="aspect-square overflow-hidden rounded border bg-[repeating-conic-gradient(#e5e7eb_0%_25%,transparent_0%_50%)] bg-[length:8px_8px]"
              title={asset.name}
            >
              {asset.previewUrl && (
                <img
                  src={asset.previewUrl}
                  alt=""
                  className="h-full w-full object-contain"
                  loading="lazy"
                />
              )}
            </span>
          ))}
        </button>
      )}

      <ul className="space-y-0.5 text-xs text-muted-foreground">
        <li>
          <button type="button" className="hover:underline" onClick={() => setTab('art')}>
            {assets.length > 0
              ? `${plural(assets.length, 'picture')} made here`
              : 'No art made here yet'}
          </button>
        </li>
        <li className="flex items-center gap-2">
          <button type="button" className="hover:underline" onClick={() => setTab('screenshots')}>
            {plural(shots, 'screenshot')}
          </button>
          {unseen > 0 && (
            <Badge variant="secondary" data-testid="forge-screenshots-unseen">
              {unseen} new
            </Badge>
          )}
        </li>
        {sending > 0 && (
          <li>
            <button type="button" className="hover:underline" onClick={() => setTab('send')}>
              {sendFiles.isRunning
                ? `Sending ${plural(sending, 'file')}`
                : `${plural(sending, 'file')} waiting for the forge to start`}
            </button>
          </li>
        )}
        {failed > 0 && (
          <li>
            <button
              type="button"
              className="text-destructive hover:underline"
              onClick={() => setTab('send')}
            >
              {plural(failed, 'file')} not sent
            </button>
          </li>
        )}
      </ul>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          onClick={() => setTab('send')}
        >
          Send files
        </Button>
        <CreativeStudioLink className="text-xs" from={from} />
      </div>

      <Dialog open={tab !== null} onOpenChange={(open) => !open && setTab(null)}>
        <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col">
          <DialogHeader>
            <DialogTitle>Art &amp; media</DialogTitle>
            <DialogDescription>
              The arche&apos;s pictures and sounds, what Claude Code saw, and what you sent in.
              {studio && (
                <>
                  {' '}
                  A picture you make in {studio.name} comes here when you press its{' '}
                  <span className="font-medium">Use in</span> button.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <Tabs
            value={tab ?? 'art'}
            onValueChange={(v) => setTab(v as ArtMediaTab)}
            className="flex min-h-0 flex-1 flex-col"
          >
            <TabsList className="self-start">
              <TabsTrigger value="art">Game art</TabsTrigger>
              <TabsTrigger value="screenshots">
                Screenshots{unseen > 0 ? ` (${unseen} new)` : ''}
              </TabsTrigger>
              <TabsTrigger value="send">Send files</TabsTrigger>
              <TabsTrigger value="styles">Player styles</TabsTrigger>
            </TabsList>
            <DialogBody className="mt-3">
              <TabsContent value="art" className="mt-0">
                <ForgeArtPanel forgeId={forgeId} {...art} />
              </TabsContent>
              <TabsContent value="screenshots" className="mt-0">
                <ForgeScreenshots
                  {...screenshots}
                  onOpen={(index) => {
                    // The viewer is a dialog of its own: this one steps aside.
                    setTab(null);
                    screenshots.onOpen(index);
                  }}
                />
              </TabsContent>
              <TabsContent value="send" className="mt-0">
                <ForgeSendFiles {...sendFiles} />
              </TabsContent>
              <TabsContent value="styles" className="mt-0">
                <ForgeShipStyle
                  archeId={archeId}
                  forgeId={forgeId}
                  {...(onAskClaude ? { onAskClaude } : {})}
                />
              </TabsContent>
            </DialogBody>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
